import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AppService } from 'app/app.service';
import { AccountFeature } from 'app/constants/constants';
import { WithAuditContext } from 'app/providers/http-mutating-operations.interceptor';
import { SemantriaAccountService } from 'app/providers/semantria-api/semantria-account.service';
import { SemantriaAPIService } from 'app/providers/semantria-api.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { SystemService } from 'app/services/system.service';
import { AccountUtils } from 'app/utils/account.utils';

export enum QueryMatching {
  HIT = 'MATCH',
  NO_HIT = 'NO_MATCH',
  MIXED = 'MIXED',
}

export interface TestDocumentGeneratorConfig {
  count: number;
  typeOfResponse: string;
  about: string;
  inStyleOf: string;
  sentiment: string;
  minSentences: number;
  maxSentences: number;
  focus: string;
  initialPhrase?: string;
  queryMatch: QueryMatching;
  query?: string;
}

interface PromptParameters {
  count: number;
  type_of_response: string;
  about: string;
  in_style_of: string;
  sentiment: string;
  min_sentences: number;
  max_sentences: number;
  focus: string;
}

interface QueryMatchPromptParameters extends PromptParameters {
  query: string;
  match_instruction: 'should match' | "shouldn't match";
}

interface ReviewGenerationResponse {
  id: string;
  results: { text: string }[];
}

interface ResultText {
  reviewtext: string[];
}

@EnableClassTelemetry()
@Injectable({ providedIn: 'root' })
export class TestDocumentGeneratorService extends SemantriaAPIService {
  private readonly DEFAULT_USER_REVIEW_GENERATION_SCEANRIO_ID =
    '9aeb50d8-1262-4318-add4-6ffc21b763f5';
  private readonly DEFAULT_USER_REVIEW_GENERATION_QUERY_MATCH_SCEANRIO_ID =
    'e3945b16-9574-421b-bf9d-5d3c345e0bff';
  private readonly DEFAULT_REPHRASE_USER_REVIEW_GENERATION_SCEANRIO_ID =
    '04d304b2-85d3-4663-b152-946a928beba9';

  private readonly FEATURE_LLM: AccountFeature = 'LARGE_LANGUAGE_MODELS';
  private readonly EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENARIO = 'SPOT_E_SCENARIO';

  constructor(
    _httpClient: HttpClient,
    _systemService: SystemService,
    private accountService: SemantriaAccountService
  ) {
    super(_httpClient, _systemService);
  }

  async hasPermission(): Promise<boolean> {
    const accountDetails = await this.accountService.sub.latest();
    return new Promise((resolve) =>
      resolve(
        AccountUtils.hasFeature(accountDetails, this.FEATURE_LLM) &&
          AccountUtils.hasExternalResource(
            accountDetails,
            AppService.getConfig().spote.userReviewGenerationScenarioId ||
              this.DEFAULT_USER_REVIEW_GENERATION_SCEANRIO_ID,
            undefined,
            this.EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENARIO
          ) &&
          AccountUtils.hasExternalResource(
            accountDetails,
            AppService.getConfig().spote.userReviewGenerationQueryMatchScenarioId ||
              this.DEFAULT_USER_REVIEW_GENERATION_QUERY_MATCH_SCEANRIO_ID,
            undefined,
            this.EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENARIO
          )
      )
    );
  }

  async hasRephrasePermission(): Promise<boolean> {
    const accountDetails = await this.accountService.sub.latest();
    return new Promise((resolve) =>
      resolve(
        AccountUtils.hasFeature(accountDetails, this.FEATURE_LLM) &&
          AccountUtils.hasExternalResource(
            accountDetails,
            AppService.getConfig().spote.rephraseUserReviewGenerationScenarioId ||
              this.DEFAULT_REPHRASE_USER_REVIEW_GENERATION_SCEANRIO_ID,
            undefined,
            this.EXTERNAL_RESOURCE_TYPE_SPOT_E_SCENARIO
          )
      )
    );
  }

  generateTestDocuments(config: TestDocumentGeneratorConfig): Promise<string[]> {
    if (config.queryMatch === QueryMatching.MIXED) {
      return this.generateWithSpotE(
        this.DEFAULT_USER_REVIEW_GENERATION_SCEANRIO_ID,
        this.getPromptParameters(config),
        this.mapSpoteJSONResponse
      );
    } else {
      return this.generateWithSpotE(
        this.DEFAULT_USER_REVIEW_GENERATION_QUERY_MATCH_SCEANRIO_ID,
        this.getQueryMatchPromptParameters(config),
        this.mapSpoteJSONResponse
      );
    }
  }

  rephraseTestDocument(testDocument: string): Promise<string> {
    return this.generateWithSpotE(
      this.DEFAULT_REPHRASE_USER_REVIEW_GENERATION_SCEANRIO_ID,
      {
        text: testDocument,
      },
      this.mapSpoteTextResponse
    );
  }

  @WithAuditContext('test-document-generated', (_, args) => {
    return { scenarioId: args[0], parameters: args[1] };
  })
  protected generateWithSpotE(
    scenarioId: string,
    parameters: any,
    responseMapperFn: (response: ReviewGenerationResponse) => any
  ): Promise<any> {
    return this.post<ReviewGenerationResponse>(`/spote/scenarios/${scenarioId}/`, parameters, {
      observe: 'body',
    }).then(responseMapperFn);
  }

  protected getPromptParameters(parameters: TestDocumentGeneratorConfig): PromptParameters {
    return {
      count: parameters.count,
      type_of_response: parameters.typeOfResponse,
      about: parameters.about,
      in_style_of: parameters.inStyleOf,
      sentiment: parameters.sentiment,
      min_sentences: parameters.minSentences,
      max_sentences: parameters.maxSentences,
      focus: parameters.focus,
    };
  }

  protected getQueryMatchPromptParameters(
    parameters: TestDocumentGeneratorConfig
  ): QueryMatchPromptParameters {
    return {
      count: parameters.count,
      type_of_response: parameters.typeOfResponse,
      about: parameters.about,
      in_style_of: parameters.inStyleOf,
      sentiment: parameters.sentiment,
      min_sentences: parameters.minSentences,
      max_sentences: parameters.maxSentences,
      focus: parameters.focus,
      query: parameters.query,
      match_instruction:
        parameters.queryMatch === QueryMatching.HIT ? 'should match' : "shouldn't match",
    };
  }

  protected mapSpoteJSONResponse(response: ReviewGenerationResponse): string[] {
    if (response?.results?.length > 0) {
      try {
        const responseJson: ResultText = JSON.parse(response.results[0].text);

        if (responseJson.reviewtext) {
          return responseJson.reviewtext;
        }

        throw new Error('Result not found in response');
      } catch {
        throw new Error('Response text is not a valid JSON');
      }
    }

    throw new Error('Result not found in response');
  }

  protected mapSpoteTextResponse(response: ReviewGenerationResponse): string {
    if (response?.results?.length > 0) {
      return response.results[0].text;
    }

    throw new Error('Result not found in response');
  }
}
